/* eslint-disable no-unused-vars */
// Import Atlassian analytics types for reference and potential future use
// These provide the strict typing for the actual analytics client
// Note: These imports are kept for reference and potential future use in type definitions
import type {
  OperationalEventPayload,
  ScreenEventPayload,
  TrackEventPayload,
  UIEventPayload,
} from '@atlassiansox/analytics-web-client';

/**
 * This file contains a comprehensive set of standard analytics event types,
 * actions, and subjects used across Atlassian. It is intentionally kept large
 * to provide a complete palette of available analytics values and ensure
 * consistency.
 *
 * For more information on analytics in this project, see src/analytics/README.md
 */
/* eslint-disable @typescript-eslint/no-explicit-any */
// past tense verbs describing the action
export const enum Action {
  Cancelled = 'cancelled',
  Clicked = 'clicked',
  Submitted = 'submitted',
  Closed = 'closed',
  Typed = 'typed',
  Searched = 'searched',
  Created = 'created',
  Updated = 'updated',
  Deleted = 'deleted',
  Invited = 'invited',
  Selected = 'selected',
  Focused = 'focused',
  Blurred = 'blurred',
  Viewed = 'viewed',
  Shown = 'shown',
  Displayed = 'displayed',
  Hovered = 'hovered',
  Entered = 'entered',
  Switched = 'switched',
  Dwelled = 'dwelled',
  Toggled = 'toggled',
  Pressed = 'pressed',
  Pasted = 'pasted',
  Copy = 'copy',
  Changed = 'changed',
  Fetch = 'fetch',
  Cleared = 'cleared',
}

export const enum ActionSubject {
  Table = 'table',
  Button = 'button',
  Form = 'form',
  Link = 'link',
  AppSwitcherDropdown = 'appSwitcherDropdown',
  AppSwitcherItem = 'appSwitcherItem',
  Organization = 'organization',
  OrganizationSelectorItem = 'organizationSelectorItem',
  BillingSelectorItem = 'billingSelectorItem',
  HelpSelectorItem = 'helpSelectorItem',
  Dialog = 'dialog',
  Datepicker = 'datepicker',
  Field = 'field',
  NavigationItem = 'navigationItem',
  Toggle = 'toggle',
  Checkbox = 'checkbox',
  DropListItem = 'dropListItem',
  DropListTrigger = 'dropListTrigger',
  RadioButton = 'radioButton',
  Radio = 'radio',
  TextField = 'textField',
  Text = 'text',
  Badge = 'badge',
  Dropdown = 'dropdown',
  DropdownItem = 'dropdownItem',
  SelectItem = 'selectItem',
  Error = 'error',
  Chart = 'chart',
  Page = 'page',
  Card = 'card',
  Tooltip = 'tooltip',
  Icon = 'icon',
  NotificationModal = 'notificationModal',
  Notification = 'notification',
  MenuItem = 'menuItem',
  UserPicker = 'userPicker',
  Accordion = 'accordion',
  Tab = 'tab',
  Modal = 'modal',
  Flag = 'flag',
  IphSideBar = 'iphSideBar',
  Input = 'input',
  SearchResult = 'searchResult',
  CallToAction = 'callToAction',
  ResendInvite = 'resendInvite',
  DisimssButton = 'dismissButton',
  EmptyTable = 'emptyTable',
  Row = 'row',
  Option = 'option',
  FilterController = 'filterController',
  Banner = 'banner',
  Spotlight = 'spotlight',
}

export type DataType = {
  action: Action;
  actionSubject: ActionSubject;
  actionSubjectId: string;
  source: string;
};

// Extracted types from FeatureEvents below to typecheck analytics function in rendered component as needed
export type FeatureEventUi = (
  attributes?: Record<string, any>,
  data?: Partial<DataType>,
) => UIEvent;

export type FeatureEvents<
  TScreenKeys extends string,
  TUiKeys extends string,
  TTrackKeys extends string,
  TOperationalKeys extends string,
> = {
  screen: Record<
    TScreenKeys,
    (attributes?: Record<string, any>) => ScreenEvent
  >;
  ui: Record<TUiKeys, FeatureEventUi>;
  track: Record<TTrackKeys, (attributes?: Record<string, any>) => TrackEvent>;
  operational: Record<
    TOperationalKeys,
    (attributes?: Record<string, any>) => OperationalEvent
  >;
};

export type BaseUIEvent = {
  data: {
    action: Action;
    actionSubject: ActionSubject;
    actionSubjectId?: string;
    source: string;
    objectType?: string;
    objectId?: string;
    containerType?: string;
    containerId?: string;
    attributes?: Record<string, any>;
    tags?: string[];
    eventType?: string;
  };
};

export type UIEvent = {
  cloudId?: string;
  orgId?: string;
  tenantId?: string;
  tenantIdType?: 'orgId' | 'cloudId';
  data: UIData;
  dataPortalLink?: string;
};

export type ScreenEvent = {
  cloudId?: string;
  orgId?: string;
  data: ScreenData;
  dataPortalLink?: string;
};

export type TrackEvent = {
  cloudId?: string;
  orgId?: string;
  data: TrackData;
  dataPortalLink?: string;
};

export type OperationalEvent = {
  cloudId?: string;
  orgId?: string;
  data: OperationalData;
  dataPortalLink?: string | string[];
  tags?: string[];
};

export type UIData = Readonly<{
  action: Action;
  actionSubject: ActionSubject;
  actionSubjectId?: string;
  source: string;
  objectType?: string;
  objectId?: string;
  object?: string;
  container?: string;
  attributes?: Record<string, any>;
  tags?: string[];
  product?: string;
  eventType?: string;
}>;

export type ScreenData = Readonly<{
  name: string;
  attributes?: Record<string, any>;
  tags?: string[];
  product?: string;
  platform?: string;
}>;

export type TrackData = Readonly<{
  source: string;
  action: string;
  actionSubject: string;
  actionSubjectId?: string;
  attributes?: Record<string, any>;
  tags?: string[];
}>;

export type OperationalData = {
  action: string;
  actionSubject: string;
  actionSubjectId?: string;
  source: string;
  attributes?: Record<string, any>;
  tags?: string[];
};

export type EngagekitEvent = Readonly<{
  payload: TrackData;
}>;
// Create a flexible type that works with both strict Atlassian types and testing
// This allows all properties to be optional for testing flexibility while maintaining
// the structure needed for the actual analytics client
export type AnalyticsEventWrapper = {
  payload: {
    type: 'screen' | 'track' | 'ui' | 'operational';
  } & (
    | (ScreenEventPayload & { type: 'screen' })
    | (TrackEventPayload & { type: 'track' })
    | (UIEventPayload & { type: 'ui' })
    | (OperationalEventPayload & { type: 'operational' })
  );
};
